import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../redux/Store/store'
import { useNavigate } from 'react-router-dom'
import DashboardBackground from '../../components/ReUsableComponents/DashboardBackground'
import SideBarV1 from '../NewDashboard/SideBarV1'
import HeaderV1 from '../NewDashboard/HeaderV1'
import PlanDetailsCard from './PlanDetailsCard'
import { setSelectedPaymentType } from '../../redux/Slices/pricingSlice'
import AuthButton from '../LogInPage/LoginButton'
import { BiArrowBack, BiX } from 'react-icons/bi'
import { useChargePaymentMutation, useGetPaymentTypesQuery, useLazyValidateCouponQuery } from '../../redux/API/Payments/paymentsapi'
import CustomTextField from '../../components/ReUsableComponents/CustomTextField'
import { formatAmount } from '../../utils'
import { toast } from 'react-toastify'
import Partypopper from '../../assets/Images/Partypopper.png'
import { getItem } from '../../utils/token'

export interface PaymentMethod {
    id: string;
    isActive: boolean;
    isEnabled: boolean;
    createdDate: string; 
    updatedDate: string | null;
    paymentType: 'ONE_TIME' | string;
    paymentLogo: string;
    paymentMode: string;
    tax: number;
    more: any | null;
}

const Checkout = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {selectedPlan,selectedPaymentType,parentStudentId,startedTestId} = useSelector((state : RootState) => ({
        selectedPlan: state.root.pricingSlice.selectedPlan,
        selectedPaymentType: state.root.pricingSlice.selectedPaymentType,
        parentStudentId:  state.root.startFullTest.parentStudentId,
        startedTestId: state.root.startFullTest.startedTestId
    }));
    const [discountCoupon,setDiscountCoupon] = React.useState<string>('');
    const [discountAmount,setDiscountAmount] = React.useState<number>(0);
    const [couponError,setCouponError] = React.useState<string>('');
    const [purchaseBtnDisabled,setPurchaseBtnDisabled] = React.useState<boolean>(false);
    const hasCouponBeenEntered  = React.useRef<boolean>(false);

    const {data : paymentMethods} = useGetPaymentTypesQuery({});
    const [validateCoupon,{isLoading}] = useLazyValidateCouponQuery();
    const [paymentCharge] = useChargePaymentMutation();

    const isFirstTest = getItem('userRole') === 'STUDENT' && startedTestId.isFirstTest;

    const handleSelectPaymentType = (paymentId : string) => {
       dispatch(setSelectedPaymentType(paymentId))
    }

    const getTotalAmount = (price : number,discount : number,tax : number) => {
        return price + tax - discount;
    }

    const handleCouponInput = (e : React.ChangeEvent<HTMLInputElement>) => {
        setDiscountCoupon(e.target.value.toUpperCase());
        if(e.target.value === ''){
            setCouponError('');
        }
    }

    const handleCoupon = async (coupon?:string) => {
        setPurchaseBtnDisabled(true);
        if(discountCoupon || coupon){
            let payload = {
                couponCode : coupon ?? discountCoupon.trim(),
                planId : selectedPlan?.id,
                amount : selectedPlan?.price,
            }
            try{
              let response = await validateCoupon(payload).unwrap();
                if(response.data){
                    setDiscountAmount(response.data.discountAmount);
                    setCouponError('');
                    hasCouponBeenEntered.current = true;
                    setPurchaseBtnDisabled(false);
                    return response.data.discountAmount;
                }
            }catch(error){
                setDiscountAmount(0);
                setPurchaseBtnDisabled(false);
                setCouponError((error as any)?.data?.message ?? 'Invalid Coupon Code');
                console.error(error)
            }
        }
    }

    const handlePayment = async (coupon?:string,discountAmountFromAPI?:number) => {
        try{
            setPurchaseBtnDisabled(true);
            const body = {
                planId: selectedPlan?.id,
                couponName: coupon ?? discountCoupon.trim(),
                actualAmount: selectedPlan?.price,
                discountAmount: discountAmountFromAPI ?? discountAmount,
                amountToBepaid: getTotalAmount(
                  Number(selectedPlan?.price),
                  discountAmountFromAPI ?? discountAmount,
                  getPaymentTypeMode('tax')
                ),
                tax: getPaymentTypeMode('tax'),
                paymentType: getPaymentTypeMode('paymentType'),
                paymentMode: getPaymentTypeMode('paymentMode'),
            };
            const params = {
                userId: getItem('userRole') === 'STUDENT' ? getItem('userId') : parentStudentId,
            };
            const payload = { body, params };
            const response = await paymentCharge(payload).unwrap();
            if(response.status === 'success'){
                window.location.href = response.data.url;
                setTimeout(() => setPurchaseBtnDisabled(false),1000);
            }
        }catch(error : any){
            console.error(error?.status);
            setPurchaseBtnDisabled(false);
            navigate('/transactionsummary/failure');
        }
    }

    const handlePurchase = async () => {
        setPurchaseBtnDisabled(true);
        if(discountCoupon !== '' && couponError === '' && hasCouponBeenEntered.current){
            await handlePayment();
        }else{
            toast(
                ({ closeToast }) => (
                  <div className="flex items-center justify-between gap-3 px-3 py-2 font-gully border-2 border-satPrepGreen rounded-md bg-white w-full" style={{ boxShadow : '0px 4px 8px 0px rgba(28, 37, 44, 0.08)'}}>
                      <img src={Partypopper} alt="partypopper" className='w-6 h-6' />
                      <p className='font-gully text-satPrepBlack text-lg'>Congratulations!! Free Coupon Code Applied.</p>
                      <button onClick={closeToast} className="text-xl hover:text-gray-500">
                        <BiX />
                      </button>
                  </div>
                ),
                {
                    closeButton: false,
                    hideProgressBar: true,
                    autoClose: 3000,
                    position: "top-right",
                    className: 'max-w-[400px] w-full mx-4',
                    style: {
                      padding: 0,
                      background: 'transparent',
                      boxShadow: 'none'
                    }
                }
            );
            applyDiscountAndPay();
        }
    };

    const applyDiscountAndPay = async () => {
        const couponCode = 'SUPERPREP';
        setDiscountCoupon(couponCode);
      
        try {
          const discountAmount = await handleCoupon(couponCode);
          setPurchaseBtnDisabled(true);
          setTimeout(() => {
            handlePayment(couponCode, discountAmount);
          }, 1500);
      
        } catch (error) {
          setPurchaseBtnDisabled(false);
          console.error('Error applying coupon or processing payment:', error);
        }
    };

    useEffect(() => {
     if(!selectedPlan) {
        navigate('/plans')
     }
    },[selectedPlan])

    const getPaymentTypeMode = (param : string) => {
        let paymentTypeData = paymentMethods?.data?.find((payment : PaymentMethod) => payment.id === selectedPaymentType);

        if(param === 'paymentMode'){
           return paymentTypeData?.paymentMode ?? ''
        }else if(param === 'paymentType'){
            return paymentTypeData?.paymentType ?? ''
        }else if(param === 'tax'){
            return paymentTypeData?.tax ?? 0
        }
    }
    
    return (
      <DashboardBackground>
        <div className={`p-5 flex gap-[1%] w-full ${isFirstTest ? 'flex-col' : 'flex-row'}`}>
          {/* Sidebar */}
          <SideBarV1 isFirstTest={isFirstTest} />

          <section
            className={`flex ${!isFirstTest && 'flex-col'} justify-center items-start gap-[2vh]`}
            style={{
              width: isFirstTest ? '100%' : "calc(100vw - 240px)",
              height: isFirstTest ? '' : "calc(100vh - 40px)",
            }}
          >
            {!isFirstTest && <HeaderV1/>}

            <div
              className="px-3 py-2 w-full flex flex-grow justify-between items-center self-stretch rounded-[28px] border-[0.5px]  border-satPrepGrey bg-satPrepTransWhite shadow-widget-card backdrop-blur-sevenFive relative"
              style={{ height: "calc(100vh - 12vh - 40px)" }}
            >
                <div className="h-full py-1 w-full overflow-y-auto scroll4">
                    <div className="flex flex-col gap-9 h-full w-full font-gully ">
                        <h2 className='text-center text-satPrepAccent text-[56px] font-bold py-3'>Checkout</h2>
                        <div className='flex gap-[2%] '>
                            <div className='flex w-[49%] h-fit p-6 flex-col gap-6 self-stretch rounded-2xl border-[0.6px] border-satPrepGrey bg-satPrepTransWhite shadow-widget-card backdrop-blur-sevenFive'>
                               <p className='text-[26px] font-semibold text-satPrepAccent'>Plan Details</p>
                               <PlanDetailsCard plan={selectedPlan}/>
                               <AuthButton 
                                 text='Back to Plans'
                                 icon={<BiArrowBack size={24} />}
                                 iconPosition='left'
                                 className='w-full !text-satPrepAccent !border !border-satPrepAccent !bg-satPrepWhite'
                                 onClick={() => navigate('/plans')}
                               />
                            </div>
                            <div className='flex w-[49%] flex-col items-start gap-3'>
                                <div className='p-6 w-full flex flex-col items-start gap-6 rounded-2xl border-[0.6px] border-satPrepGrey  bg-satPrepTransWhite shadow-widget-card backdrop-blur-sevenFive'>
                                    <p className='text-satPrepAccent text-[26px] font-semibold'>Payment Method</p>
                                    <div className='space-y-4'>
                                        {
                                            paymentMethods?.data?.map((payment : PaymentMethod) => (
                                                <div className='flex items-center gap-3'>
                                                    <label className="role">
                                                        <span className={`ml-3 mr-9 font-medium text-lg flex gap-[6px] ${!payment?.isEnabled && 'text-satPrepGrey'}`}><img src={payment.paymentLogo} alt={payment.paymentMode} className={`w-6 h-6 ${!payment?.isEnabled && 'grayscale opacity-20'}`}/>{payment.paymentMode}</span>
                                                        <input
                                                            value="paymentType"
                                                            type="radio"
                                                            name="radio"
                                                            checked={selectedPaymentType === payment.id}
                                                            onChange={() => handleSelectPaymentType(payment.id)}
                                                            disabled={!payment.isEnabled}
                                                        />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>

                                <div className='flex p-6 gap-[6px] w-full rounded-2xl border-[0.6px] border-satPrepGrey  bg-satPrepTransWhite shadow-widget-card backdrop-blur-sevenFive relative'>
                                    <CustomTextField
                                        onChangeHandler={(e) => handleCouponInput(e)}
                                        placeholder='Discount Coupon'
                                        type={"text"}
                                        name="discountCoupon"
                                        mandatory={true}
                                        value={discountCoupon}
                                        className="w-full !border-satPrepDarkGrey"
                                        isError={couponError !== ''}
                                    />
                                    {
                                        couponError !== '' && <p className='absolute text-satPrepRed text-xs bottom-2 left-7'>{couponError}</p>
                                    }
                                    <AuthButton
                                      text='Apply'
                                      onClick={() => handleCoupon()}
                                      className='h-full'
                                      loader={isLoading}
                                      disabled={discountCoupon.trim() === ''}
                                    />
                                </div>

                                <div className='flex flex-col p-6 gap-6 rounded-2xl border-[0.6px] border-satPrepGrey  bg-satPrepTransWhite shadow-widget-card backdrop-blur-sevenFive w-full'>
                                    <div className='flex flex-col gap-6'>
                                        <p className='text-[26px] font-semibold text-satPrepAccent'>Purchase Summary</p>
                                        <div className='flex flex-col gap-[6px]'>
                                            <div className='flex justify-between items-center'>
                                                <p className='text-xl font-medium'>{selectedPlan?.planName}</p>
                                                <p>{selectedPlan?.currencySymbol}{selectedPlan?.price}</p>
                                            </div>

                                            <div className='flex justify-between items-center'>
                                                <p className='text-base font-normal'>Discount</p>
                                                <p>{selectedPlan?.currencySymbol}{formatAmount(discountAmount)}</p>
                                            </div>

                                            <div className='flex justify-between items-center'>
                                                <p className='text-base font-normal'>Tax</p>
                                                <p>{selectedPlan?.currencySymbol}{formatAmount(getPaymentTypeMode('tax'))}</p>
                                            </div>
                                        </div>
                                        <div className='w-full h-[0.5px] bg-satPrepGrey'></div>
                                        <div className='flex justify-between items-center text-satPrepXDarkAccent text-[22px] font-medium'>
                                           <p>Total</p>
                                           <p>{selectedPlan?.currencySymbol}{formatAmount(getTotalAmount(Number(selectedPlan?.price),discountAmount,getPaymentTypeMode('tax')))}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-3 w-full">
                                        <div className="w-fit">
                                            <AuthButton
                                                text="Cancel"
                                                onClick={() => navigate('/plans')}
                                                className='!border !border-[#828285] !bg-satPrepLightGrey !text-satPrepBlack !py-[13px] !px-[22px]'
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <AuthButton
                                                text="Complete Purchase"
                                                iconPosition="right"
                                                icon={<div className="rotate-180"><BiArrowBack size={24} /></div>}
                                                onClick={handlePurchase}
                                                className="w-full"
                                                disabled={selectedPaymentType.trim() === '' || isLoading || purchaseBtnDisabled}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
          </section>
        </div>
      </DashboardBackground>
    );
}

export default Checkout